import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail, ChevronRight } from "lucide-react";
import { getPlatformSettings } from "@/lib/admin/queries";
import { updatePlatformSettings } from "@/lib/admin/actions";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const emailSchema = z.object({
  from_name: z.string().min(1, "From name is required"),
  from_email: z.string().email("Must be a valid email"),
  email_provider: z.enum(["resend", "smtp"]),
  resend_api_key: z.string().optional(),
  smtp_host: z.string().optional(),
  smtp_port: z.coerce.number().optional(),
  smtp_username: z.string().optional(),
  smtp_password: z.string().optional(),
});

type EmailForm = z.infer<typeof emailSchema>;

export default function SettingsEmail() {
  const { profile } = useAuth();
  const actorId = profile?.id ?? "";
  const qc = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: ["admin", "platform-settings"],
    queryFn: getPlatformSettings,
  });

  const settings = settingsQuery.data?.data ?? {};

  const form = useForm<EmailForm>({
    resolver: zodResolver(emailSchema),
    values: {
      from_name: settings.from_name ?? "Stratakey",
      from_email: settings.from_email ?? "noreply@stratakey.ca",
      email_provider: settings.email_provider ?? "resend",
      resend_api_key: settings.resend_api_key ?? "",
      smtp_host: settings.smtp_host ?? "",
      smtp_port: settings.smtp_port ?? 587,
      smtp_username: settings.smtp_username ?? "",
      smtp_password: settings.smtp_password ?? "",
    },
  });

  const provider = form.watch("email_provider");

  const saveMutation = useMutation({
    mutationFn: (data: EmailForm) => updatePlatformSettings(data, actorId),
    onSuccess: () => {
      toast.success("Email configuration saved");
      qc.invalidateQueries({ queryKey: ["admin", "platform-settings"] });
    },
    onError: () => toast.error("Failed to save email configuration"),
  });

  return (
    <div className="p-6 space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin/settings">Settings</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator>
            <ChevronRight className="h-3.5 w-3.5" />
          </BreadcrumbSeparator>
          <BreadcrumbItem>
            <BreadcrumbPage>Email Configuration</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex items-center gap-3">
        <Mail className="h-6 w-6 text-gray-600" />
        <h1 className="text-2xl font-bold text-gray-900">Email Configuration</h1>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="text-base">Outbound Email Settings</CardTitle>
        </CardHeader>
        <CardContent>
          {settingsQuery.isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="space-y-1">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-9 w-full" />
                </div>
              ))}
            </div>
          ) : (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit((data) => saveMutation.mutate(data))}
                className="space-y-5"
              >
                <FormField
                  control={form.control}
                  name="from_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="whitespace-nowrap">From name</FormLabel>
                      <FormControl>
                        <Input placeholder="Stratakey" {...field} />
                      </FormControl>
                      <p className="text-xs text-gray-400">
                        This name appears as the sender in email clients.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="from_email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="whitespace-nowrap">From email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="noreply@stratakey.ca" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email_provider"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="whitespace-nowrap">Email provider</FormLabel>
                      <FormControl>
                        <RadioGroup
                          value={field.value}
                          onValueChange={field.onChange}
                          className="flex gap-6 mt-1"
                        >
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="resend" id="ep-resend" />
                            <Label htmlFor="ep-resend" className="text-sm font-normal cursor-pointer">Resend</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="smtp" id="ep-smtp" />
                            <Label htmlFor="ep-smtp" className="text-sm font-normal cursor-pointer">SMTP</Label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {provider === "resend" && (
                  <FormField
                    control={form.control}
                    name="resend_api_key"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="whitespace-nowrap">Resend API key</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            placeholder="re_…"
                            autoComplete="new-password"
                            {...field}
                          />
                        </FormControl>
                        <p className="text-xs text-gray-400">
                          Find your API key at resend.com/api-keys
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {provider === "smtp" && (
                  <div className="space-y-4 rounded-md border border-gray-200 p-4 bg-gray-50">
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">SMTP Configuration</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="col-span-2">
                        <FormField
                          control={form.control}
                          name="smtp_host"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="whitespace-nowrap">Host</FormLabel>
                              <FormControl>
                                <Input placeholder="smtp.example.com" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <FormField
                        control={form.control}
                        name="smtp_port"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="whitespace-nowrap">Port</FormLabel>
                            <FormControl>
                              <Input type="number" placeholder="587" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="smtp_username"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="whitespace-nowrap">Username</FormLabel>
                          <FormControl>
                            <Input placeholder="user@example.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="smtp_password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="whitespace-nowrap">Password</FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              autoComplete="new-password"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
                  <Button type="submit" className="whitespace-nowrap" disabled={saveMutation.isPending}>
                    {saveMutation.isPending ? "Saving…" : "Save Configuration"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="whitespace-nowrap"
                    onClick={() => {
                      toast.success(`Test email sent to ${profile?.email ?? "your account email"}`);
                    }}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Send Test Email
                  </Button>
                </div>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-gray-400 max-w-2xl">
        Changes take effect immediately for new emails. Existing queued emails use the previous configuration.
      </p>
    </div>
  );
}
